import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ImportantNewsModal = ({ message, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        message: '',
        is_active: true,
        display_order: 0
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (message) {
            setFormData({
                message: message.message || '',
                is_active: message.is_active ?? true,
                display_order: message.display_order || 0
            });
        }
    }, [message]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.message.trim()) {
            toast.error('Message is required');
            return;
        }

        if (formData.message.length > 500) {
            toast.error('Message must be 500 characters or less');
            return;
        }

        try {
            setSaving(true);
            if (message) {
                // Update existing message
                await axios.put(`/api/admin/important-news/${message.id}`, formData);
                toast.success('Message updated successfully');
            } else {
                // Create new message
                await axios.post('/api/admin/important-news', formData);
                toast.success('Message created successfully');
            }
            onSave();
        } catch (error) {
            console.error('Error saving message:', error);
            toast.error(error.response?.data?.message || 'Failed to save message');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {message ? 'Edit Message' : 'Add New Message'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6 text-slate-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Message *
                        </label>
                        <textarea
                            value={formData.message}
                            onChange={(e) => handleChange('message', e.target.value)}
                            rows={4}
                            maxLength={500}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                            placeholder="Enter important news message..."
                            required
                        />
                        <div className="flex justify-end mt-1">
                            <span className={`text-xs ${formData.message.length > 450 ? 'text-red-600' : 'text-slate-500'
                                }`}>
                                {formData.message.length}/500 characters
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                value={formData.display_order}
                                onChange={(e) => handleChange('display_order', parseInt(e.target.value))}
                                min="0"
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Status
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer mt-3">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => handleChange('is_active', e.target.checked)}
                                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                                />
                                <span className="text-sm text-slate-700">Active</span>
                            </label>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : (message ? 'Update Message' : 'Create Message')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ImportantNewsModal;
